/**
 * ApiClient — HTTP client with automatic headers, retry logic, and request cancellation
 */

const RETRY_CONFIG = {
  maxRetries: 2,
  retryDelay: 1000,
  retryableStatuses: [500, 502, 503, 504],
};

export default class ApiClient {
  /**
   * @param {string} baseUrl - API base URL (no trailing slash)
   * @param {string} apiKey - Shop API key
   */
  constructor(baseUrl, apiKey) {
    this._baseUrl = baseUrl;
    this._apiKey = apiKey;
    this._sessionKey = null;
    this._activeControllers = new Set();
  }

  /**
   * Set session key for Authorization header
   * @param {string|null} key
   */
  setSessionKey(key) {
    this._sessionKey = key;
  }

  /**
   * GET request
   * @param {string} endpoint - API path
   * @param {Object} [params] - Query parameters
   * @param {Object} [options] - { signal }
   * @returns {Promise<Object>} Parsed response data
   */
  async get(endpoint, params, options = {}) {
    let url = this._baseUrl + endpoint;
    if (params) {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined) {
          query.append(key, value);
        }
      }
      const qs = query.toString();
      if (qs) url += '?' + qs;
    }
    return this._request(url, { method: 'GET' }, options);
  }

  /**
   * POST request with JSON body
   * @param {string} endpoint - API path
   * @param {Object} [body] - Request body
   * @param {Object} [options] - { signal }
   * @returns {Promise<Object>} Parsed response data
   */
  async post(endpoint, body, options = {}) {
    const url = this._baseUrl + endpoint;
    return this._request(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      },
      options
    );
  }

  /**
   * POST request with FormData (multipart)
   * @param {string} endpoint - API path
   * @param {FormData} formData
   * @param {Object} [options] - { signal }
   * @returns {Promise<Object>} Parsed response data
   */
  async postFormData(endpoint, formData, options = {}) {
    const url = this._baseUrl + endpoint;
    // Don't set Content-Type — browser sets it with boundary for multipart
    return this._request(
      url,
      {
        method: 'POST',
        body: formData,
      },
      options
    );
  }

  /**
   * Create an AbortController for cancellable requests
   * @returns {AbortController}
   */
  createAbortController() {
    const controller = new AbortController();
    this._activeControllers.add(controller);
    return controller;
  }

  /**
   * Abort all active requests (used on destroy)
   */
  abortAll() {
    for (const controller of this._activeControllers) {
      controller.abort();
    }
    this._activeControllers.clear();
  }

  /**
   * @private
   * Core request method with headers, retry, and error handling
   */
  async _request(url, fetchOptions, { signal } = {}) {
    const controller = this.createAbortController();
    // If external signal provided, forward abort
    if (signal) {
      if (signal.aborted) {
        controller.abort();
      } else {
        signal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }

    const headers = {
      'X-API-Key': this._apiKey,
      ...fetchOptions.headers,
    };

    if (this._sessionKey) {
      headers['Authorization'] = `Bearer ${this._sessionKey}`;
    }

    const options = {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    };

    try {
      return await this._requestWithRetry(url, options, 0);
    } finally {
      this._activeControllers.delete(controller);
    }
  }

  /**
   * @private
   * Execute request with retry logic for 5xx errors
   */
  async _requestWithRetry(url, options, attempt) {
    let response;
    try {
      response = await fetch(url, options);
    } catch (err) {
      if (err.name === 'AbortError') {
        throw err;
      }
      // Network error — retry if attempts remain
      if (attempt < RETRY_CONFIG.maxRetries) {
        await this._delay(RETRY_CONFIG.retryDelay);
        return this._requestWithRetry(url, options, attempt + 1);
      }
      throw new ApiError('Network error', 0, null, err);
    }

    // Parse response body
    let body;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    // Success
    if (response.ok) {
      return body;
    }

    // Retryable 5xx
    if (
      RETRY_CONFIG.retryableStatuses.includes(response.status) &&
      attempt < RETRY_CONFIG.maxRetries
    ) {
      await this._delay(RETRY_CONFIG.retryDelay);
      return this._requestWithRetry(url, options, attempt + 1);
    }

    // Non-retryable error
    const message =
      (body && (body.error || body.detail)) || `HTTP ${response.status}`;
    throw new ApiError(message, response.status, body);
  }

  /** @private */
  _delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  /**
   * @param {string} message
   * @param {number} status - HTTP status code (0 for network errors)
   * @param {Object|null} body - Parsed response body
   * @param {Error} [cause] - Original error
   */
  constructor(message, status, body, cause) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.code = body?.code || null;
    if (cause) this.cause = cause;
  }
}
